<template>
  <li class="collection-item">
    <a class="collection-link" @click="$emit('onClickCard', id)">
      <div class="collection-card">
        <!-- Thumbnail -->
        <div class="collection-card__thumbnail">
          <img :src="image" :alt="title" class="collection-card__image">
          <div class="collection-card__overlay"></div>
          <span class="collection-card__badge">
            {{ counter }} {{ $t('general.content') }}
          </span>
          <div class="collection-card__play-icon">
            <i class="uil-layers"></i>
          </div>

          <!-- Status Badges -->
          <span v-if="showRejectedBadge" class="collection-card__status-badge collection-card__status-badge--danger">
            <i class="uil-times-circle"></i>
            {{ $t('general.rejected_by_admin') }}
          </span>
          <span v-else-if="showPendingBadge" class="collection-card__status-badge collection-card__status-badge--warning">
            <i class="uil-clock"></i>
            {{ $t('general.admin_approval_awaited') }}
          </span>
        </div>

        <!-- Body -->
        <div class="collection-card__body">
          <h4 class="collection-card__title">{{ title }}</h4>
          <div class="collection-card__meta">
            <i class="uil-calendar-alt"></i>
            <span>{{ collectionCreatedAt }}</span>
          </div>
        </div>
      </div>
    </a>
  </li>
</template>

<script>
import moment from "moment";
import { MOMENT_DATE_FORMAT_FOR_KT } from "@/core/config/constant";

export default {
  name: "Collection",

  props: {
    collection: {
      required: true,
      type: Object
    }
  },

  computed: {
    id() {
      return this.collection.id;
    },

    title() {
      return this.collection.name;
    },

    image() {
      return this.collection.cover_image_link;
    },

    date() {
      return this.collection.created_at;
    },

    counter() {
      const collectionContents = this.collection.collection_contents_count || 0;
      const trainingContents = this.collection.collection_training_contents_count || 0;
      const externalContents = this.collection.external_contents_count || 0;
      return collectionContents + trainingContents + externalContents;
    },

    isVerified() {
      return this.collection.is_verified;
    },

    verifiedAt() {
      return this.collection.verified_at;
    },

    collectionCreatedAt() {
      return moment(this.date).format(MOMENT_DATE_FORMAT_FOR_KT);
    },

    showRejectedBadge() {
      return this.isVerified === 0 && this.verifiedAt !== null;
    },

    showPendingBadge() {
      return this.isVerified === 0 && this.verifiedAt === null;
    }
  }
};
</script>

<style scoped>
.collection-item {
  list-style: none;
  padding: 0;
  margin: 0;
  height: 100%;
}

.collection-link {
  display: block;
  text-decoration: none;
  cursor: pointer;
  height: 100%;
}

.collection-card {
  background: #ffffff;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.collection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.collection-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Thumbnail */
.collection-card__thumbnail {
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  overflow: hidden;
}

.collection-card__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.collection-card:hover .collection-card__image {
  transform: scale(1.05);
}

.collection-card__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.collection-card:hover .collection-card__overlay {
  opacity: 1;
}

.collection-card__badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 6px;
  letter-spacing: 0.3px;
  z-index: 2;
}

.collection-card__play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2;
}

.collection-card__play-icon i {
  font-size: 20px;
  color: var(--backgroundColor, #667eea);
}

.collection-card:hover .collection-card__play-icon {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Body */
.collection-card__body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.collection-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.collection-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
}

.collection-card__meta i {
  font-size: 14px;
  color: #94a3b8;
}

/* Status Badge - Positioned on thumbnail */
.collection-card__status-badge {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 6px;
  backdrop-filter: blur(4px);
  z-index: 2;
  letter-spacing: 0.3px;
}

.collection-card__status-badge i {
  font-size: 12px;
}

.collection-card__status-badge--danger {
  background: rgba(220, 38, 38, 0.9);
  color: #ffffff;
}

.collection-card__status-badge--warning {
  background: rgba(217, 119, 6, 0.9);
  color: #ffffff;
}

/* Responsive */
@media (max-width: 959px) {
  .collection-card:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .collection-card__body {
    padding: 14px;
  }

  .collection-card__title {
    font-size: 16px;
  }

  .collection-card__meta {
    font-size: 12px;
  }
}

@media (max-width: 639px) {
  .collection-card {
    border-radius: 14px;
  }

  .collection-card__body {
    padding: 12px;
  }

  .collection-card__title {
    font-size: 16px;
    line-height: 1.4;
  }

  .collection-card__meta {
    min-height: 24px;
    font-size: 12px;
    line-height: 1.5;
  }

  .collection-card__meta i {
    font-size: 16px;
  }

  .collection-card__badge {
    font-size: 12px;
    padding: 6px 10px;
    top: 8px;
    left: 8px;
  }

  .collection-card__play-icon {
    width: 40px;
    height: 40px;
  }

  .collection-card__play-icon i {
    font-size: 18px;
  }

  .collection-card__status-badge {
    bottom: 8px;
    right: 8px;
    font-size: 12px;
    padding: 6px 10px;
    gap: 4px;
  }

  .collection-card__status-badge i {
    font-size: 14px;
  }
}
</style>
